import { cn } from "@/lib/utils";
import { Icon as PhosphorIcon, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { AnimatedCounter } from "@/components/ui/animated-counter";

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: PhosphorIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  className?: string;
}

export function StatCard({ title, value, subtitle, icon: Icon, trend, className }: StatCardProps) {
  const numericValue = typeof value === "number" ? value : null;

  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      className={cn(
        "group relative bg-card border border-border rounded-2xl p-6 overflow-hidden",
        "hover:border-white/[0.12] transition-colors duration-300",
        className,
      )}
    >
      {/* Subtle radial accent — appears on hover */}
      <div
        aria-hidden
        className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
      />

      <div className="relative flex items-start justify-between gap-3">
        <div className="space-y-1.5 min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            {title}
          </p>
          <p className="text-3xl font-semibold text-foreground tracking-tight" style={{ fontVariantNumeric: "tabular-nums" }}>
            {numericValue !== null ? <AnimatedCounter value={numericValue} /> : value}
          </p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          {trend && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
              <span
                className={cn(
                  "inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-md",
                  trend.isPositive
                    ? "text-success bg-success/15"
                    : "text-destructive bg-destructive/15",
                )}
              >
                {trend.isPositive ? (
                  <ArrowUp weight="bold" className="w-3 h-3" />
                ) : (
                  <ArrowDown weight="bold" className="w-3 h-3" />
                )}
                {trend.value}%
              </span>
              <span className="text-xs text-muted-foreground">vs sem. passada</span>
            </div>
          )}
        </div>
        <div className="p-2.5 rounded-lg bg-primary/10 shrink-0 ring-1 ring-primary/10 group-hover:ring-primary/25 group-hover:bg-primary/15 transition-all duration-300">
          <Icon weight="duotone" className="w-5 h-5 text-primary" />
        </div>
      </div>
    </motion.div>
  );
}
